import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    LinearProgress
} from '@mui/material';
import { format, startOfMonth } from 'date-fns';
import { Project, TimeEntry } from '../../types/collaborator';
import { createTimeEntry, getTimeEntries, getUserProjects } from '../../services/collaborator';
import { formatDateBR } from '../../utils/date';

// Data local (toISOString() usa UTC: à noite no Brasil já era o dia seguinte)
const today = () => format(new Date(), 'yyyy-MM-dd');

const TimeEntrySection: React.FC = () => {
    const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string>(today());
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [hours, setHours] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    // Mês corrente por padrão (antes: só o dia de hoje, e lançamentos de ontem "sumiam")
    const [startDate, setStartDate] = useState<string>(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState<string>(today());

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [projectsData, entriesData] = await Promise.all([
                getUserProjects(),
                getTimeEntries(startDate, endDate)
            ]);
            setProjects(projectsData);
            setTimeEntries(entriesData);
            setError(null);
        } catch (err) {
            setError('Erro ao carregar dados. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openNewEntry = () => {
        setFormError(null);
        setSelectedDate(today());
        setSelectedProject(projects.length === 1 ? String(projects[0].id) : '');
        setHours('');
        setDescription('');
        setOpenDialog(true);
    };

    const hoursValue = parseFloat(hours);
    const hoursInvalid = hours !== '' && (isNaN(hoursValue) || hoursValue <= 0 || hoursValue > 24);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedDate || !selectedProject || !hours || !description.trim()) {
            setFormError('Preencha data, projeto, horas e descrição.');
            return;
        }
        if (hoursInvalid) {
            setFormError('Informe entre 0,5 e 24 horas.');
            return;
        }

        try {
            setSaving(true);
            await createTimeEntry({
                project_id: parseInt(selectedProject),
                date: selectedDate,
                hours: hoursValue,
                description: description.trim()
            });
            setOpenDialog(false);
            // Garante que o lançamento apareça na lista mesmo fora do período filtrado
            if (selectedDate < startDate) setStartDate(selectedDate);
            if (selectedDate > endDate) setEndDate(selectedDate);
            fetchData();
        } catch (err: any) {
            // Mostra a regra do backend (ex.: projeto sem alocação, data futura) dentro do diálogo
            setFormError(err?.response?.data?.error || 'Erro ao criar lançamento. Por favor, tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    const totalHours = timeEntries.reduce((sum, entry) => sum + Number(entry.hours || 0), 0);

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={1}>
                        <Typography variant="h6">
                            Lançamento de Horas
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={openNewEntry}
                            disabled={loading}
                        >
                            Novo Lançamento
                        </Button>
                    </Box>
                    {!loading && projects.length === 0 && (
                        <Alert severity="info">
                            Você ainda não está alocado(a) em nenhum projeto. Peça ao gestor do projeto para
                            alocá-lo(a) em uma posição (aba Equipe do projeto) para poder lançar horas.
                        </Alert>
                    )}
                </Grid>

                <Grid item xs={12}>
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <TextField
                            label="Data Inicial"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: '1 1 150px' }}
                        />
                        <TextField
                            label="Data Final"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: '1 1 150px' }}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {loading && <LinearProgress sx={{ mb: 1 }} />}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Projeto</TableCell>
                                    <TableCell>Horas</TableCell>
                                    <TableCell>Descrição</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {timeEntries.map((entry) => (
                                    <TableRow key={entry.id}>
                                        <TableCell>
                                            {formatDateBR(entry.date)}
                                        </TableCell>
                                        <TableCell>{entry.project_name}</TableCell>
                                        <TableCell>{entry.hours}</TableCell>
                                        <TableCell>{entry.description}</TableCell>
                                    </TableRow>
                                ))}
                                {!loading && timeEntries.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            Nenhum lançamento encontrado para o período selecionado.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {timeEntries.length > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={2}><b>Total no período</b></TableCell>
                                        <TableCell colSpan={2}><b>{totalHours}h</b></TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit} noValidate>
                    <DialogTitle>Novo Lançamento de Horas</DialogTitle>
                    <DialogContent>
                        {formError && <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>}
                        <Grid container spacing={3} sx={{ mt: 0 }}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Data"
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: today() }}
                                    fullWidth
                                    required
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel id="time-entry-project-label">Projeto</InputLabel>
                                    <Select
                                        labelId="time-entry-project-label"
                                        value={selectedProject}
                                        label="Projeto"
                                        onChange={(e) => setSelectedProject(String(e.target.value))}
                                    >
                                        {projects.map((project) => (
                                            <MenuItem key={project.id} value={String(project.id)}>
                                                {project.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {projects.length === 0 && (
                                        <FormHelperText>Nenhum projeto com alocação ativa.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Horas"
                                    type="number"
                                    value={hours}
                                    onChange={(e) => setHours(e.target.value)}
                                    inputProps={{ step: "0.5", min: "0.5", max: "24" }}
                                    error={hoursInvalid}
                                    helperText={hoursInvalid ? 'Entre 0,5 e 24 horas' : ''}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Descrição"
                                    multiline
                                    rows={4}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                        <Button type="submit" variant="contained" color="primary" disabled={saving}>
                            Salvar
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default TimeEntrySection;
